import { createFileRoute, Link, Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@/lib/auth/hooks/useAuth';
import LoadingCard from '@/lib/loading/components/LoadingCard';
import { Round } from '@/lib/models/round';
import useRound from '@/lib/rounds/hooks/useRound';
import { formatTemperateFehrenheit, formatWind } from '@/lib/utils/roundUtils';
import { formatDate } from '@/machinery/date';
import useAxios from '@/machinery/useAxios';
import { faArrowLeft, faChevronLeft, faPencil, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import { useCallback, useMemo, useState } from 'react';
import EditRoundForm from '@/components/rounds/EditRoundForm';
import { parseDate, CalendarDate } from '@internationalized/date';
import { Button, useDisclosure } from '@heroui/react';
import DeleteRoundModal from '@/lib/rounds/components/DeleteRoundModal';
import useIsMobile from '@/lib/common/hooks/useIsMoible';

export const Route = createFileRoute('/rounds/$roundId')({
    component: RouteComponent,
});

function RouteComponent() {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const navigate = useNavigate();
    const { ax, loading } = useAxios();
    const { user } = useAuth();
    const { roundId } = Route.useParams();
    const roundIdNumber = useMemo(() => parseInt(roundId ?? '0'), [roundId]);
    const { round, isLoading, refetch } = useRound({ id: roundIdNumber });
    const pathname = useLocation();
    const editing = useMemo(() => pathname.pathname?.includes('/hole/'), [pathname.pathname]);
    const [editDetails, setEditDetails] = useState(false);

    const onSubmitDetails = useCallback(
        async (data: {
            round_date: CalendarDate;
            number_of_holes: number;
            round_type: string;
            wind?: number;
            wind_gusts?: number;
            temperature?: number;
            course_id?: number;
        }) => {
            await ax<Round>({
                method: 'put',
                url: data.course_id ? `/rounds/${roundId}` : `/rounds/${roundId}/v2`,
                data,
            });
            refetch();
        },
        [ax, refetch, roundId],
    );

    const handleDelete = useCallback(async () => {
        await ax({
            url: `/rounds/${roundId}`,
            method: 'delete',
            callback: () => {
                refetch();
                onClose();
                navigate({ to: '/rounds' });
            },
        });
    }, [ax, refetch, onClose, roundId, navigate]);

    const canEdit = useMemo(() => {
        return round?.user.id === user?.id;
    }, [round?.user.id, user?.id]);

    const teesLabel = useMemo(() => {
        if (!round?.club) return undefined;

        const allClubTees = round.club.courses?.map(course => course.tees).flat();

        const front9Tee = allClubTees?.find(tee => tee.id == round.front_9_tee_id);
        const back9Tee = allClubTees?.find(tee => tee.id == round.back_9_tee_id);
        const full18Tee = allClubTees?.find(tee => tee.id == round.full_round_tee_id);

        if (full18Tee) return full18Tee.name;

        if (front9Tee && !back9Tee) return front9Tee.name;
        if (back9Tee && !front9Tee) return back9Tee.name;
        if (!front9Tee || !back9Tee) return undefined;
        return `${front9Tee.name} / ${back9Tee.name}`;
    }, [round]);

    const isMobile = useIsMobile();

    return (
        <>
            <div className='mx-auto mt-1 px-1 md:max-w-7xl   '>
                <Link to={'/rounds'}>
                    <Button
                        variant='light'
                        color='primary'
                        startContent={<FontAwesomeIcon icon={faArrowLeft} className='pr-2' />}>
                        Back to rounds
                    </Button>
                </Link>
                <div className='simple-card md:md-3 min-h-192 m-1 p-2 md:mx-auto md:max-w-7xl md:p-5'>
                    {isLoading || !round ? (
                        <LoadingCard />
                    ) : (
                        <div>
                            <div className='flex w-full flex-col items-start gap-2 text-2xl font-semibold md:flex-row md:items-center'>
                                {round.course?.name ?? round.club?.name}
                                {teesLabel && (
                                    <span className='text-sm text-gray-500'>{`(${teesLabel})`}</span>
                                )}
                                <div className='flex w-full flex-1 flex-row items-center justify-between gap-2 '>
                                    {canEdit && (
                                        <Button
                                            variant='light'
                                            color='primary'
                                            size='sm'
                                            onPress={() => setEditDetails(true)}>
                                            Edit Course
                                            <FontAwesomeIcon icon={faPencil} />
                                        </Button>
                                    )}
                                    {canEdit && (
                                        <Button variant='light' color='danger' size='sm' onPress={onOpen}>
                                            Delete Round
                                            <FontAwesomeIcon icon={faTrashCan} />
                                        </Button>
                                    )}
                                </div>
                            </div>

                            {editDetails && (
                                <EditRoundForm
                                    onCancel={() => setEditDetails(false)}
                                    defaultValues={{
                                        course_id: round.course?.id,
                                        number_of_holes: round.number_of_holes,
                                        round_date: parseDate(dayjs(round.round_date).format('YYYY-MM-DD')),
                                        round_type: round.round_type ?? 'practice',
                                        temperature: round.temperature,
                                        wind: round.wind,
                                        wind_gusts: round.wind_gusts,
                                        front_9_tee_id: round.front_9_tee_id,
                                        back_9_tee_id: round.back_9_tee_id,
                                        full_round_tee_id: round.full_round_tee_id,
                                        club_id: round.club?.id,
                                    }}
                                    onSubmit={onSubmitDetails}
                                    loading={loading}
                                    course={round.course}
                                    oldCourseForm={!!round.course?.id}
                                    submitText='Update'
                                />
                            )}
                            <div className='flex flex-col items-stretch gap-2 md:flex-row md:items-center md:justify-between'>
                                <div className='grid flex-1'>
                                    <div className='font-semibold'>{round.user.full_name}</div>
                                    <div>
                                        {round?.round_date && formatDate(round?.round_date)} -{' '}
                                        {round?.number_of_holes} holes -{' '}
                                        {round?.round_type === 'practice' ? 'Practice' : 'Tournament'}
                                    </div>
                                    <div className='grid grid-cols-3 text-xs'>
                                        {round.temperature && (
                                            <span>Temp: {formatTemperateFehrenheit(round.temperature)}</span>
                                        )}
                                        {round.wind && <span>Wind: {formatWind(round.wind)}</span>}
                                        {round.wind_gusts && (
                                            <span>Gusts: {formatWind(round.wind_gusts)}</span>
                                        )}
                                    </div>
                                </div>
                                {canEdit && (
                                    <div className=''>
                                        {!editing ? (
                                            <Link
                                                to='/rounds/$roundId/hole/$holeNumber'
                                                params={{ roundId: roundId, holeNumber: '1' }}>
                                                <Button
                                                    color='primary'
                                                    startContent={
                                                        <FontAwesomeIcon icon={faPencil} className='pr-2' />
                                                    }
                                                    fullWidth={isMobile}>
                                                    Edit shots
                                                </Button>
                                            </Link>
                                        ) : (
                                            <Link to='/rounds/$roundId' params={{ roundId: roundId }}>
                                                <Button
                                                    color='primary'
                                                    variant='solid'
                                                    startContent={
                                                        <FontAwesomeIcon
                                                            icon={faChevronLeft}
                                                            className='pr-2'
                                                        />
                                                    }
                                                    fullWidth={isMobile}>
                                                    Back to summary
                                                </Button>
                                            </Link>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    <Outlet />
                </div>
            </div>
            <DeleteRoundModal
                handleDelete={handleDelete}
                isOpen={isOpen}
                onClose={onClose}
                loading={loading}
            />
        </>
    );
}
